import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="post-harvest--classification"
// One quality per container, or a split into portions that share its weight.
export default class extends Controller {
  static targets = ["containerList", "containersDataField"];
  static values = {
    toleranceGrams: { type: Number, default: 0.01 },
    leftTemplate: String,
    overTemplate: String,
  };

  connect() {
    this.portionCounter = 0;
    this.containerListTarget
      .querySelectorAll("[data-container-block]")
      .forEach((block) => this.#updateGap(block));
    this.serialize();
  }

  update() {
    this.containerListTarget
      .querySelectorAll("[data-container-block]")
      .forEach((block) => this.#updateGap(block));
    this.serialize();
  }

  split(event) {
    event.preventDefault();
    const block = event.target.closest("[data-container-block]");
    if (!block) return;

    const list = block.querySelector("[data-portion-list]");
    if (list && list.querySelectorAll("[data-portion-row]").length === 0) {
      this.#appendPortion(
        block,
        this.#sourceValue(block),
        this.#fieldValue(block, "[data-unsplit-panel]", "crop_quality_id"),
        this.#photoSignedId(block.querySelector("[data-unsplit-panel]")),
      );
    }

    block.querySelector("[data-unsplit-panel]")?.classList.add("hidden");
    block.querySelector("[data-split-panel]")?.classList.remove("hidden");
    this.#updateGap(block);
    this.serialize();
  }

  addPortion(event) {
    event.preventDefault();
    const block = event.target.closest("[data-container-block]");
    if (!block) return;

    const remainder = Math.max(0, this.#sourceValue(block) - this.#usedValue(block));
    this.#appendPortion(block, remainder, "", "");
    this.#updateGap(block);
    this.serialize();
  }

  removePortion(event) {
    event.preventDefault();
    const block = event.target.closest("[data-container-block]");
    const row = event.target.closest("[data-portion-row]");
    if (!block || !row) return;

    const rows = [...block.querySelectorAll("[data-portion-row]")];
    if (rows.length <= 2) {
      const remaining = rows.find((candidate) => candidate !== row) || row;
      this.#copyPortionToUnsplit(block, remaining);
      rows.forEach((candidate) => candidate.remove());
      block.querySelector("[data-split-panel]")?.classList.add("hidden");
      block.querySelector("[data-unsplit-panel]")?.classList.remove("hidden");
    } else {
      row.remove();
    }

    this.#updateGap(block);
    this.serialize();
  }

  useRemainder(event) {
    event.preventDefault();
    const block = event.target.closest("[data-container-block]");
    const row = event.target.closest("[data-portion-row]");
    if (!block || !row) return;

    const remainder = Math.max(0, this.#sourceValue(block) - this.#usedValue(block, row));
    const input = row.querySelector("[data-field=weight_value]");
    if (input) input.value = this.#formatInput(remainder);
    this.#updateGap(block);
    this.serialize();
  }

  serialize() {
    const blocks = this.containerListTarget.querySelectorAll("[data-container-block]");
    const containers = [];

    blocks.forEach((block) => {
      const containerId = parseInt(block.dataset.containerId, 10);
      const rows = [...block.querySelectorAll("[data-portion-row]")];
      if (this.#splitOpen(block) && rows.length >= 2) {
        containers.push({
          container_id: containerId,
          portions: rows.map((row) => ({
            crop_quality_id: this.#integerOrNull(row.querySelector("[data-field=crop_quality_id]")?.value),
            weight_value: row.querySelector("[data-field=weight_value]")?.value || null,
            weight_unit: block.dataset.weightUnit || "g",
            classification_photo_signed_id: this.#photoSignedId(row),
          })),
        });
        return;
      }

      const source = this.#splitOpen(block) && rows.length === 1
        ? rows[0]
        : block.querySelector("[data-unsplit-panel]");
      containers.push({
        container_id: containerId,
        crop_quality_id: this.#integerOrNull(source?.querySelector("[data-field=crop_quality_id]")?.value),
        classification_photo_signed_id: this.#photoSignedId(source),
      });
    });

    this.containersDataFieldTarget.value = JSON.stringify(containers);
  }

  #appendPortion(block, weight, qualityId, photoSignedId) {
    const template = block.querySelector("[data-portion-template]");
    const list = block.querySelector("[data-portion-list]");
    if (!template || !list) return;

    this.portionCounter += 1;
    const markup = template.content.firstElementChild.outerHTML.replace(
      /NEW_ID/g,
      `${Date.now()}-${this.portionCounter}`,
    );
    list.insertAdjacentHTML("beforeend", markup);
    const row = list.lastElementChild;
    const weightInput = row.querySelector("[data-field=weight_value]");
    if (weightInput && weight !== "" && weight !== null && weight !== undefined) {
      weightInput.value = this.#formatInput(weight);
    }
    const quality = row.querySelector("[data-field=crop_quality_id]");
    if (quality && qualityId) quality.value = String(qualityId);
    this.#copyPhoto(block.querySelector("[data-unsplit-panel]"), row, photoSignedId);
  }

  #copyPortionToUnsplit(block, row) {
    const panel = block.querySelector("[data-unsplit-panel]");
    if (!panel || !row) return;

    const quality = panel.querySelector("[data-field=crop_quality_id]");
    const portionQuality = row.querySelector("[data-field=crop_quality_id]")?.value;
    if (quality) quality.value = portionQuality || "";
    this.#copyPhoto(row, panel, this.#photoSignedId(row));
  }

  #copyPhoto(fromPanel, toPanel, signedId) {
    if (!toPanel) return;

    const signed = toPanel.querySelector("[data-post-harvest--photo-upload-target='signedId']");
    if (signed) signed.value = signedId || "";
    if (!signedId || !fromPanel) return;

    const fromImage = fromPanel.querySelector("[data-post-harvest--photo-upload-target='image']");
    const toImage = toPanel.querySelector("[data-post-harvest--photo-upload-target='image']");
    const toPreview = toPanel.querySelector("[data-post-harvest--photo-upload-target='preview']");
    const toInput = toPanel.querySelector("[data-post-harvest--photo-upload-target='inputWrapper']");
    if (!fromImage?.getAttribute("src") || !toImage) return;

    toImage.src = fromImage.src;
    toPreview?.classList.remove("hidden");
    toInput?.classList.add("hidden");
  }

  #updateGap(block) {
    const gap = block.querySelector("[data-gap]");
    if (!gap) return;
    if (!this.#splitOpen(block)) {
      gap.classList.add("hidden");
      return;
    }

    const gramsPerUnit = parseFloat(block.dataset.gramsPerUnit) || 1;
    const sourceGrams = parseFloat(block.dataset.weightGrams) || 0;
    const difference = sourceGrams - this.#usedValue(block) * gramsPerUnit;
    if (Math.abs(difference) <= this.toleranceGramsValue) {
      gap.classList.add("hidden");
      return;
    }

    const amount = Math.abs(difference) / gramsPerUnit;
    const formatted = `${new Intl.NumberFormat(document.documentElement.lang || "en", {
      maximumFractionDigits: 2,
    }).format(amount)} ${block.dataset.weightUnit || "g"}`;
    const template = difference > 0 ? this.leftTemplateValue : this.overTemplateValue;
    gap.textContent = template.replace("__WEIGHT__", formatted);
    gap.classList.remove("hidden");
    gap.classList.toggle("text-warning", difference > 0);
    gap.classList.toggle("text-error", difference < 0);
  }

  #splitOpen(block) {
    const panel = block.querySelector("[data-split-panel]");
    return Boolean(panel) && !panel.classList.contains("hidden");
  }

  #sourceValue(block) {
    return parseFloat(block.dataset.weightValue) || 0;
  }

  #usedValue(block, exceptRow) {
    let used = 0;
    block.querySelectorAll("[data-portion-row]").forEach((row) => {
      if (row === exceptRow) return;
      used += parseFloat(row.querySelector("[data-field=weight_value]")?.value) || 0;
    });
    return used;
  }

  #fieldValue(block, panelSelector, field) {
    return block.querySelector(`${panelSelector} [data-field=${field}]`)?.value || "";
  }

  #photoSignedId(panel) {
    return panel?.querySelector("[data-post-harvest--photo-upload-target='signedId']")?.value || null;
  }

  #integerOrNull(value) {
    return value ? parseInt(value, 10) : null;
  }

  #formatInput(value) {
    const rounded = Math.round((Number(value) + Number.EPSILON) * 100) / 100;
    return String(rounded);
  }
}
